import React, { useState, useEffect } from 'react';
import { FileText, Printer, Download } from 'lucide-react';
import api from '@/api/api';
import { useNavigate } from 'react-router-dom';
import { generateBookingReceipt } from '@/utils/bookingReceiptGenerator';
import { getCollegeInfo, fetchCollegeInfo } from '@/utils/receiptHeaderUtils';
import Pagination from '@/components/Pagination';
import { exportTableToPDF } from '@/utils/pdfExport';
import * as XLSX from 'xlsx';
import { formatDate } from '../../utils/dateTimeUtils';

const BookingReport = () => {
  const [bookings, setBookings] = useState([]);
  const [courses, setCourses] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [collegeInfo, setCollegeInfoState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total_bookings: 0, converted: 0, pending: 0, total_amount: 0 });
  const [filters, setFilters] = useState({
    from_date: '',
    to_date: '',
    status: 'all'
  });
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchCourses();
    fetchConsultants();
    fetchSessions();
    loadCollegeInfo();
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [currentPage, itemsPerPage, filters]);

  const loadCollegeInfo = async () => {
    try {
      // Always fetch fresh college info for current tenant
      const response = await api.get('/tenants/my-college/settings');
      if (response.data) {
        const info = {
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        };
        setCollegeInfoState(info);
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
      // No fallback - leave as null, which will show warning in receipt generation
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await api.get('/master-settings/courses');
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await api.get('/consultants/');
      setConsultants(response.data);
    } catch (error) {
      console.error('Error fetching consultants:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await api.get('/master-settings/sessions');
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      const response = await api.get(`/seat-booking/report?${params.toString()}`);
      
      // Handle response
      let bookingsData = [];
      if (Array.isArray(response.data)) {
        bookingsData = response.data;
        setTotalItems(response.pagination?.total || response.data.length);
      } else if (response.data) {
        bookingsData = response.data.bookings || [];
        setTotalItems(response.data.total || 0);
        setStats({
          total_bookings: response.data.total_bookings || 0,
          converted: response.data.converted || 0,
          pending: response.data.pending || 0,
          total_amount: response.data.total_amount || 0
        });
      }
      
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const getCourseName = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course?.name || 'N/A';
  };

  const getSession = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    return course?.session || new Date().getFullYear();
  };

  const getConsultantName = (consultantId) => {
    if (!consultantId) return 'N/A';
    const consultant = consultants.find(c => c.id === consultantId);
    return consultant?.name || 'N/A';
  };

  const handlePrintReceipt = async (booking) => {
    // Get session name from sessions array if session_id exists, otherwise use booking.session
    const sessionObj = sessions.find(s => s.id === booking.session_id);
    const sessionName = sessionObj?.name || booking.session || 'N/A';
    
    // Fetch course fee from fee_structures (session-aware)
    let courseFee = booking.course_fee || 0;
    if (!courseFee && booking.course_id) {
      try {
        const feeRes = await api.get(`/master-settings/fee-structures`, {
          params: { course_id: booking.course_id }
        });
        const feeData = Array.isArray(feeRes.data) ? feeRes.data : (feeRes.data?.data || []);
        if (feeData.length > 0) {
          // Match by session_id first
          if (booking.session_id) {
            const sessionMatch = feeData.find(f => f.session_id === booking.session_id);
            if (sessionMatch) {
              courseFee = sessionMatch.total_fee || sessionMatch.total_amount || 0;
            }
          }
          // Fallback to first fee
          if (!courseFee) {
            courseFee = feeData[0]?.total_fee || feeData[0]?.total_amount || 0;
          }
        }
      } catch (err) {
        console.error('Error fetching fee for receipt:', err);
      }
    }
    
    const receiptData = {
      ...booking,
      course_name: getCourseName(booking.course_id),
      session: sessionName,
      session_name: sessionName,
      course_fee: parseFloat(booking.course_fee) > 0 ? parseFloat(booking.course_fee) : courseFee,
      consultant_name: getConsultantName(booking.consultant_id)
    };
    
    // Pass fresh college info to ensure correct tenant's details
    await generateBookingReceipt(receiptData, collegeInfo);
  };

  // Convert to admission removed - only available from Booking List page

  const getBookingExportData = () => {
    return bookings.map((b, i) => ({
      'Sl No': i + 1,
      'Receipt No': b.receipt_no || 'N/A',
      'Student Name': b.student_name || `${b.first_name || ''} ${b.middle_name || ''} ${b.last_name || ''}`.trim(),
      'Course': getCourseName(b.course_id),
      'Entry Type': b.is_lateral_entry ? `Lateral (Sem ${b.entry_semester})` : 'Regular',
      'Session': b.session || 'N/A',
      'Consultant': getConsultantName(b.consultant_id),
      'Booking Amount': b.booking_amount || 0,
      'Booking Date': b.booking_date ? formatDate(b.booking_date) : 'N/A'
    }));
  };

  const handleExportExcel = () => {
    const data = getBookingExportData();
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Booking Report');
    XLSX.writeFile(wb, `Booking_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportPDF = () => {
    const columns = [
      { header: 'Sl No', dataKey: 'Sl No' },
      { header: 'Receipt No', dataKey: 'Receipt No' },
      { header: 'Student Name', dataKey: 'Student Name' },
      { header: 'Course', dataKey: 'Course' },
      { header: 'Entry Type', dataKey: 'Entry Type' },
      { header: 'Booking Amount', dataKey: 'Booking Amount' },
      { header: 'Booking Date', dataKey: 'Booking Date' }
    ];
    const data = getBookingExportData();
    exportTableToPDF('Booking Report', columns, data, `Booking_Report_${new Date().toISOString().slice(0, 10)}.pdf`, collegeInfo);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Booked': 'bg-blue-100 text-blue-800',
      'Converted': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Converted Bookings Report</h1>
          <p className="text-sm text-gray-500 mt-1">View all bookings that have been converted to admissions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportExcel}
            disabled={bookings.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm disabled:opacity-50"
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4" />
            Excel
          </button>
          <button
            onClick={handleExportPDF}
            disabled={bookings.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm disabled:opacity-50"
            data-testid="export-pdf-btn"
          >
            <FileText className="h-4 w-4" />
            PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={filters.from_date}
              onChange={(e) => handleFilterChange('from_date', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              value={filters.to_date}
              onChange={(e) => handleFilterChange('to_date', e.target.value)}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => { setCurrentPage(1); fetchBookings(); }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Report Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Booking Records
          </h2>
          <div className="text-sm text-gray-600">
            Total Bookings: {bookings.length}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consultant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    Loading converted bookings...
                  </td>
                </tr>
              ) : bookings.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    No converted bookings found
                  </td>
                </tr>
              ) : (
                bookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {booking.receipt_no || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {booking.student_name || `${booking.first_name || ''} ${booking.middle_name || ''} ${booking.last_name || ''}`.trim()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {getCourseName(booking.course_id)}
                    </td>
                    <td className="px-6 py-4">
                      {booking.is_lateral_entry ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          Lateral (Sem {booking.entry_semester})
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Regular
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {booking.session || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {getConsultantName(booking.consultant_id)}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      ₹{booking.booking_amount?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatDate(booking.booking_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePrintReceipt(booking)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-white text-sm rounded hover:bg-blue-700"
                          title="Print Receipt"
                        >
                          <Printer size={14} />
                          Print
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / itemsPerPage)}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>
    </div>
  );
};

export default BookingReport;
